import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Search, X, Loader2 } from "lucide-react";

export default function SearchBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Close on escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") { setOpen(false); setQuery(""); setResults([]); } };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const closeSearch = () => { setOpen(false); setQuery(""); setResults([]); };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="p-2 text-[#a19d98] hover:text-[#b8966a] transition-colors"
        data-testid="search-toggle"
        aria-label="Search"
      >
        <Search size={18} />
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-[#0c0c0c]/80 backdrop-blur-sm z-[60]" onClick={closeSearch} />

      {/* Search Panel */}
      <div className="fixed top-0 left-0 right-0 z-[70] pt-4 px-4 md:pt-8 md:px-8" data-testid="search-panel">
        <div className="max-w-3xl mx-auto">
          {/* Input */}
          <div className="bg-[#161616] border border-[#b8966a]/20 flex items-center gap-3 px-5">
            <Search size={20} className="text-[#b8966a] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search arrangements, bouquets, events..."
              className="flex-1 bg-transparent border-none py-4 text-[#f8f5f0] font-body text-lg placeholder:text-[#a19d98]/60 focus:outline-none"
              data-testid="search-input"
            />
            {loading && <Loader2 size={18} className="animate-spin text-[#b8966a] flex-shrink-0" />}
            <button onClick={closeSearch} className="p-1 text-[#a19d98] hover:text-[#f8f5f0] transition-colors" data-testid="search-close">
              <X size={20} />
            </button>
          </div>

          {/* Results */}
          {query.length >= 2 && (
            <div className="bg-[#161616] border border-t-0 border-[#b8966a]/20 max-h-[60vh] overflow-y-auto">
              {results.length === 0 && !loading ? (
                <div className="p-8 text-center">
                  <p className="font-body text-[#a19d98]">No results found for "{query}"</p>
                </div>
              ) : (
                <div className="divide-y divide-[#b8966a]/10">
                  {results.map((product) => (
                    <Link
                      key={product.id}
                      to={`/collection/${product.id}`}
                      onClick={closeSearch}
                      className="flex items-center gap-5 p-4 hover:bg-[#b8966a]/5 transition-colors group"
                      data-testid={`search-result-${product.id}`}
                    >
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-14 h-14 object-cover flex-shrink-0 border border-[#b8966a]/10"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-[#f8f5f0] group-hover:text-[#b8966a] transition-colors truncate">
                          {product.name}
                        </p>
                        <p className="font-sans text-xs text-[#a19d98] uppercase tracking-wider">
                          {product.category?.replace("_", " ")}
                        </p>
                      </div>
                      <p className="font-sans text-sm text-[#b8966a] flex-shrink-0">
                        ${product.price?.toFixed(2)}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hint */}
          {query.length < 2 && (
            <div className="bg-[#161616] border border-t-0 border-[#b8966a]/20 p-6 text-center">
              <p className="font-body text-sm text-[#a19d98]">Type at least 2 characters to search</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
